/**
 * Enhanced Error Boundary Component with Persian Language Support
 * Catches JavaScript errors anywhere in the child component tree
 * Provides user-friendly error messages with retry options
 * Supports Persian text rendering and RTL layout
 * Integrated with ErrorLoggingService for comprehensive error tracking
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import { PersianText } from './shared/PersianText';
import { errorLoggingService, ErrorContext } from '../services/errorLoggingService';
import {
  isModuleLoadError,
  markReactErrorBoundaryCaught,
  reloadOnceForStaleChunk,
} from '../utils/moduleLoadError';

export interface ErrorDetails {
  message: string;
  stack?: string;
  componentStack?: string;
  timestamp: string;
  userAgent: string;
  url: string;
  userId?: string;
  sessionId?: string;
}

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showDetails?: boolean;
  level?: 'page' | 'component' | 'section';
  context?: string;
  component?: string;
  severity?: 'low' | 'medium' | 'high' | 'critical';
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  errorId?: string;
  retryCount: number;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { 
      hasError: false,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    markReactErrorBoundaryCaught();
    return { 
      hasError: true, 
      error,
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    markReactErrorBoundaryCaught();
    if (isModuleLoadError(error)) {
      console.warn('ErrorBoundary: stale or failed module load, reloading once', error);
      if (reloadOnceForStaleChunk()) {
        return;
      }
    }
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Create enhanced error context for ErrorLoggingService
    const errorContext: ErrorContext = {
      source: 'ErrorBoundary',
      category: 'react_error_boundary',
      component: this.props.component || 'Unknown',
      severity: this.props.severity || 'high',
      tags: [
        'error-boundary',
        'react-error',
        this.props.level || 'component',
        `retry-count-${this.state.retryCount}`
      ],
      additionalData: {
        componentStack: errorInfo.componentStack,


        level: this.props.level,
        context: this.props.context,
        retryCount: this.state.retryCount,
        userAgent: navigator.userAgent,
        url: window.location.href,
        userId: this.getUserId(),
        sessionId: this.getSessionId(),
        timestamp: new Date().toISOString()
      }
    };

    // Report error using enhanced ErrorLoggingService
    const errorId = errorLoggingService.logComponentError(
      error,
      this.props.component || 'ErrorBoundary',
      errorContext
    );
    
    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
    
    this.setState({
      error,
      errorInfo,
      errorId
    });
  }

  private getUserId(): string | undefined {
    try {
      const user = JSON.parse(localStorage.getItem('userProfile') || '{}');
      return user.id;
    } catch {
      return undefined;
    }
  }

  private getSessionId(): string | undefined {
    return sessionStorage.getItem('sessionId') || undefined;
  }

  private handleRetry = () => {
    // Log retry attempt
    if (this.state.error) {
      const errorContext: ErrorContext = {
        source: 'ErrorBoundary',
        category: 'error_recovery',
        component: this.props.component || 'Unknown',
        severity: 'medium',
        tags: ['error-boundary', 'retry-attempt', `attempt-${this.state.retryCount + 1}`],
        additionalData: {
          originalError: this.state.error.message,
          retryCount: this.state.retryCount + 1,
          level: this.props.level
        }
      };

      errorLoggingService.logInfo(
        `Error boundary retry attempt ${this.state.retryCount + 1}`,
        'component',
        errorContext
      );
    }

    if (isModuleLoadError(this.state.error) && reloadOnceForStaleChunk()) {
      return;
    }

    this.setState({
      hasError: false,
      retryCount: this.state.retryCount + 1
    });
  };

  private handleReload = () => {
    // Log page reload attempt
    if (this.state.error) {
      const errorContext: ErrorContext = {
        source: 'ErrorBoundary',
        category: 'error_recovery',
        component: this.props.component || 'Unknown',
        severity: 'medium',
        tags: ['error-boundary', 'page-reload', 'recovery-action'],
        additionalData: {
          originalError: this.state.error.message,
          retryCount: this.state.retryCount,
          level: this.props.level,
          recoveryMethod: 'page_reload'
        }
      };

      errorLoggingService.logInfo(
        'Error boundary triggered page reload',
        'component',
        errorContext
      );
    }

    window.location.reload();
  };

  private handleGoBack = () => {
    // Log navigation back attempt
    if (this.state.error) {
      const errorContext: ErrorContext = {
        source: 'ErrorBoundary',
        category: 'error_recovery',
        component: this.props.component || 'Unknown',
        severity: 'low',
        tags: ['error-boundary', 'navigation-back', 'recovery-action'],
        additionalData: {
          originalError: this.state.error.message,
          retryCount: this.state.retryCount,
          level: this.props.level,
          recoveryMethod: 'navigation_back',
          hasHistory: window.history.length > 1
        }
      };

      errorLoggingService.logInfo(
        'Error boundary triggered navigation back',
        'component',
        errorContext
      );
    }

    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = '/';
    }
  };

  private handleReportIssue = () => {
    if (this.state.errorId) {
      // Log issue report attempt
      const errorContext: ErrorContext = {
        source: 'ErrorBoundary',
        category: 'user_feedback',
        component: this.props.component || 'Unknown',
        severity: 'low',
        tags: ['error-boundary', 'issue-report', 'user-action'],
        additionalData: {
          originalError: this.state.error?.message,
          errorId: this.state.errorId,
          retryCount: this.state.retryCount,
          level: this.props.level,
          reportMethod: 'email'
        }
      };

      errorLoggingService.logInfo(
        'User initiated error report',
        'component',
        errorContext
      );

      const subject = encodeURIComponent(`System Error - ${this.state.errorId}`);
      const body = encodeURIComponent(`
Error Description: ${this.state.error?.message || 'Unknown error'}
Error ID: ${this.state.errorId}
Time: ${new Date().toLocaleString('en-US')}
Page: ${window.location.href}

Please provide a complete description of the actions that led to this error:
      `);
      
      window.open(`mailto:support@persianist.com?subject=${subject}&body=${body}`);
    }
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isPageLevel = this.props.level === 'page';
      const containerClass = isPageLevel 
        ? "min-h-screen bg-gray-100 p-8" 
        : "w-full bg-red-50 border border-red-200 rounded-lg p-6 my-4";

      // Default fallback UI with Persian support
      return (
        <div className={containerClass} dir="rtl">
          <div className={isPageLevel ? "max-w-2xl mx-auto" : "w-full"}>
            <div className={isPageLevel ? "bg-red-50 border border-red-200 rounded-lg p-6" : ""}>
              <div className="flex items-center mb-4">
                <svg className="w-8 h-8 text-red-600 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span className="text-xl font-semibold text-red-800">
                  An error occurred
                </span>
              </div>
              
              <p className="text-red-600 mb-4">
                Unfortunately, an error occurred while displaying this section. Please try again.
              </p>

              {this.state.errorId && (
                <div className="mb-4 p-3 bg-red-100 rounded">
                  <span className="text-sm text-red-700 font-mono">
                    {`Error ID: ${this.state.errorId}`}
                  </span>
                </div>
              )}
              
              {this.props.showDetails && this.state.error && (
                <details className="mb-4">
                  <summary className="cursor-pointer text-red-700 font-medium mb-2">
                    Error Details
                  </summary>
                  <pre className="bg-red-100 p-3 rounded text-sm text-red-800 overflow-auto max-h-40">
                    {this.state.error.toString()}
                  </pre>
                </details>
              )}

              {this.props.showDetails && this.state.errorInfo && (
                <details className="mb-4">
                  <summary className="cursor-pointer text-red-700 font-medium mb-2">
                    <PersianText>پشته کامپوننت</PersianText>
                  </summary>
                  <pre className="bg-red-100 p-3 rounded text-sm text-red-800 overflow-auto max-h-40">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}

              <div className="flex flex-wrap gap-3">
                <button
                  onClick={this.handleRetry}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center"
                >
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <PersianText>تلاش مجدد</PersianText>
                </button>
                
                <button
                  onClick={this.handleReload}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors flex items-center"
                >
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <PersianText>بارگذاری مجدد صفحه</PersianText>
                </button>
                
                <button
                  onClick={this.handleGoBack}
                  className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors flex items-center"
                >
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  <PersianText>بازگشت</PersianText>
                </button>

                <button
                  onClick={this.handleReportIssue}
                  className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors flex items-center"
                >
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <PersianText>گزارش مشکل</PersianText>
                </button>
              </div>

              {this.state.retryCount > 0 && (
                <div className="mt-4 p-3 bg-yellow-100 border border-yellow-300 rounded">
                  <PersianText className="text-sm text-yellow-800">
                    {`تعداد تلاش‌های انجام شده: ${this.state.retryCount}`}
                  </PersianText>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}



export default ErrorBoundary;